import type { ReactNode } from 'react'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import {
  SplitViewHeader,
  SplitViewHeaderTitle,
  SplitViewHeaderSubtitle,
} from '@/components/SplitView/SplitViewHeader'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { Tooltip } from '@/components/Tooltip'
import { Spinner } from '@/components/Spinner'
import styles from './Header.module.css'

export interface HeaderProps {
  isProgressShown: boolean
  progressPercent: number
  backTooltip: string
  isBackDisabled: boolean
  onBack: () => unknown
  title: string
  persistingStatus?: ReactNode
  isActionsMenuShown: boolean
  isActionsMenuDisabled: boolean
  onRename: () => unknown
  onClone: () => unknown
  onDelete: () => unknown
}

export function Header({
  isProgressShown,
  progressPercent,
  backTooltip,
  isBackDisabled,
  onBack,
  title,
  persistingStatus,
  isActionsMenuShown,
  isActionsMenuDisabled,
  onRename,
  onClone,
  onDelete,
}: HeaderProps) {
  const testId = 'draft-header'
  return (
    <SplitViewHeader progress={isProgressShown ? progressPercent : undefined}>
      <div className={styles.root}>
        <div className={styles.extra}>
          <Tooltip placement="bottom" content={backTooltip}>
            <IconButton
              name="back"
              label="Back"
              onPress={onBack}
              isDisabled={isBackDisabled}
            />
          </Tooltip>
        </div>
        <div className={styles.main}>
          <SplitViewHeaderTitle>{title}</SplitViewHeaderTitle>
          {isProgressShown && (
            <SplitViewHeaderSubtitle>
              {progressPercent}% completed
            </SplitViewHeaderSubtitle>
          )}
        </div>
        <div className={styles.extra}>
          {persistingStatus}
          {isActionsMenuShown && (
            <Dropdown
              position="bottom-end"
              autoDismiss
              isDisabled={isActionsMenuDisabled}
              button={
                <IconButton
                  name="more"
                  label="Draft actions"
                  testId={`${testId}-actions-dropdown-toggle`}
                  rotate={90}
                />
              }
            >
              <DropdownItem
                data-test={`${testId}-rename-job-button`}
                icon={<Icon name="rename-job" />}
                onPress={onRename}
                shortcut="F2"
              >
                Rename
              </DropdownItem>
              <DropdownItem
                data-test={`${testId}-duplicate-button`}
                icon={<Icon name="copy" />}
                onPress={onClone}
              >
                Clone
              </DropdownItem>
              <DropdownItem icon={<Icon name="delete" />} onPress={onDelete}>
                Delete
              </DropdownItem>
            </Dropdown>
          )}
        </div>
      </div>
    </SplitViewHeader>
  )
}

export interface PersistingStatusProps {
  error?: boolean
  persisting?: boolean
  title?: string
  children?: ReactNode
}

export function PersistingStatus({
  error,
  persisting,
  title,
  children,
}: PersistingStatusProps) {
  return (
    <div className={cx(styles.status, error && styles.error)}>
      {error && (
        <div className={styles.statusIcon}>
          <Icon name="info" />
        </div>
      )}
      {persisting && (
        <div className={styles.statusIcon}>
          <Spinner size={16} />
        </div>
      )}
      <div className={styles.message} title={title}>
        {children}
      </div>
    </div>
  )
}
